import React from 'react';
import {
  FormControl,
  Grid,
  FormErrorMessage,
  FormLabel,
  Input,
  CurrencyInput,
  InputRightAddon,
} from '@wegroup/design-system';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CurrencyDurationRightAddon from '../CurrencyDurationRightAddon';
import useAdvisoryReportCurrency from '../../../../../hooks/useAdvisoryReportCurrency';
import useRiskObjectInputStore from '@parties/src/features/risk-objects/store/riskObjectInputStore';

interface Props {
  showAllFields?: boolean;
}

const AddInsuranceModalInsuranceFormPremiumFields: React.FC<Props> = ({
  showAllFields,
}) => {
  const setFocusedInput = useRiskObjectInputStore(
    (state) => state.setFocusedInput,
  );
  const { t } = useTranslation();
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { symbol } = useAdvisoryReportCurrency();

  const deductibleError = errors?.deductible?.message || '';

  return (
    <Grid templateColumns={{ base: '1fr', tablet: '1fr 1fr' }} gridGap="4">
      <Controller
        name="premium"
        control={control}
        render={({
          field: { onChange, ...fieldProps },
          fieldState: { error },
        }) => {
          return (
            <FormControl isInvalid={!!error} gridColumn="1/-1">
              <FormLabel>{t('PREMIUM')}</FormLabel>
              <CurrencyInput
                symbol={symbol}
                placeholder="0,00"
                rightAddon={
                  // wrapping it here, otherwise chakra doesn't recognise the inputrightAddon component
                  <InputRightAddon
                    w={{ base: '32', tablet: '44' }}
                    p="0"
                    bg="unset"
                    overflow="hidden"
                    _hover={{
                      border: '1px',
                    }}
                  >
                    <CurrencyDurationRightAddon
                      value={watch('payment_interval')}
                      onChange={(e) =>
                        setValue('payment_interval', e.currentTarget.value, {
                          shouldTouch: true,
                          shouldDirty: true,
                        })
                      }
                      onBlur={() => setFocusedInput(null)}
                      onFocus={() => setFocusedInput('payment_interval')}
                    />
                  </InputRightAddon>
                }
                onChange={({ value }) => onChange(value)}
                {...fieldProps}
                onBlur={() => setFocusedInput(null)}
                onFocus={() => setFocusedInput('premium')}
              />
              {error?.message && (
                <FormErrorMessage>{t(error.message)}</FormErrorMessage>
              )}
            </FormControl>
          );
        }}
      />
      {showAllFields && (
        <>
          <Controller
            name="insured_value"
            control={control}
            render={({
              field: { onChange, ...fieldProps },
              fieldState: { error },
            }) => {
              return (
                <FormControl isInvalid={!!error}>
                  <FormLabel>
                    {t(
                      'ADVISORY-REPORTS_CURRENT-INSURANCE_MODAL_DETAIL_INSURED-VALUE',
                    )}
                  </FormLabel>
                  <CurrencyInput
                    placeholder="0,00"
                    onChange={({ value }) => onChange(value)}
                    symbol={symbol}
                    {...fieldProps}
                  />
                  {error?.message && (
                    <FormErrorMessage>{t(error.message)}</FormErrorMessage>
                  )}
                </FormControl>
              );
            }}
          />

          <FormControl isInvalid={!!deductibleError}>
            <FormLabel>
              {t('ADVISORY-REPORTS_CURRENT-INSURANCE_MODAL_DETAIL_OWN-RISK')}
            </FormLabel>
            <Input
              placeholder={t(
                'ADVISORY-REPORTS_CURRENT-INSURANCE_MODAL_DETAIL_PLACEHOLDER',
              )}
              {...register('deductible')}
            />
            {deductibleError && (
              <FormErrorMessage>
                {t(deductibleError as string)}
              </FormErrorMessage>
            )}
          </FormControl>
        </>
      )}
    </Grid>
  );
};

export default AddInsuranceModalInsuranceFormPremiumFields;
